import { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from '@/lib/supabase.js'
import {
  getRoom,
  addRoomPlayer,
  removeRoomPlayer,
  getRoomPlayers,
  updateRoomStatus,
  updateRoomGame,
} from '@/utils/roomApi.js'

/**
 * Manages room state, player presence, and Supabase realtime subscriptions.
 * Uses a stable sessionId per tab + upsert so StrictMode and page refreshes
 * don't accidentally remove the player record.
 */
export function useRoom(roomId, role) {
  const [room, setRoom] = useState(null)
  const [players, setPlayers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const mountedRef = useRef(true)
  const registeredRef = useRef(false)
  const [sessionId] = useState(() => Math.random().toString(36).slice(2, 10))

  useEffect(() => {
    mountedRef.current = true
    return () => { mountedRef.current = false }
  }, [])

  // ─── initial load ─────────────────────────────────────────────────────────

  useEffect(() => {
    if (!roomId) return

    async function init() {
      setLoading(true)
      try {
        const { data: roomData, error: roomErr } = await getRoom(roomId)
        if (roomErr) throw roomErr
        if (!mountedRef.current) return
        setRoom(roomData)
        const { data: playersData } = await getRoomPlayers(roomId)
        if (mountedRef.current) setPlayers(playersData || [])
      } catch (err) {
        if (mountedRef.current) setError(err.message || 'Failed to load room')
      } finally {
        if (mountedRef.current) setLoading(false)
      }
    }

    init()
  }, [roomId])

  // ─── player registration ──────────────────────────────────────────────────

  useEffect(() => {
    if (!roomId || !role) return
    let cancelled = false

    const handleUnload = () => {
      if (registeredRef.current) removeRoomPlayer(roomId, role)
    }

    async function register() {
      const { error: addErr } = await addRoomPlayer(roomId, role, sessionId)
      if (addErr) {
        console.error('[useRoom] Failed to register player:', addErr.message)
        return
      }
      if (!cancelled) {
        registeredRef.current = true
        const { data } = await getRoomPlayers(roomId)
        if (!cancelled && mountedRef.current) setPlayers(data || [])
      }
    }

    register()
    window.addEventListener('beforeunload', handleUnload)

    return () => {
      cancelled = true
      window.removeEventListener('beforeunload', handleUnload)
      if (registeredRef.current) {
        registeredRef.current = false
        removeRoomPlayer(roomId, role)
      }
    }
  }, [roomId, role, sessionId])

  // ─── realtime subscription ────────────────────────────────────────────────

  useEffect(() => {
    if (!roomId) return

    const channel = supabase.channel(`room:${roomId}`, {
      config: { broadcast: { self: false } },
    })

    channel
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'rooms', filter: `id=eq.${roomId}` },
        (payload) => {
          if (!mountedRef.current) return
          if (payload.new) setRoom(payload.new)
        }
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'room_players', filter: `room_id=eq.${roomId}` },
        async () => {
          if (!mountedRef.current) return
          const { data } = await getRoomPlayers(roomId)
          if (mountedRef.current) setPlayers(data || [])
        }
      )
      .subscribe()

    return () => { supabase.removeChannel(channel) }
  }, [roomId])

  // ─── derived state ────────────────────────────────────────────────────────

  const guestPlayer = players.find((p) => p.role === 'guest')
  const creatorPlayer = players.find((p) => p.role === 'creator')
  const guestPeerId = guestPlayer?.peer_id || null
  const creatorPeerId = creatorPlayer?.peer_id || null
  const guestJoined = !!guestPlayer

  // ─── actions ──────────────────────────────────────────────────────────────

  const startGame = useCallback(() => updateRoomStatus(roomId, 'active'), [roomId])
  const finishGame = useCallback(
    (result) =>
      updateRoomGame(roomId, {
        status: 'finished',
        game_result: typeof result === 'object' ? JSON.stringify(result) : result,
      }),
    [roomId]
  )
  const restartGame = useCallback(
    () => updateRoomGame(roomId, { status: 'active', game_result: null, current_fen: null }),
    [roomId]
  )
  const closeRoom = useCallback(() => updateRoomStatus(roomId, 'closed'), [roomId])

  return {
    room,
    players,
    loading,
    error,
    sessionId,
    guestPeerId,
    creatorPeerId,
    guestJoined,
    startGame,
    finishGame,
    restartGame,
    closeRoom,
  }
}
